import { Flex, Select, Typography } from "antd";
import { GlobeSimpleIcon } from "@phosphor-icons/react";
import { useTranslation } from "@refinedev/core";
import { useSystemConfiguration, useTranslate } from "#src/hooks";
import type { SelectProps } from "antd";
import {
  getLanguageName,
  withEnglishFirst,
  withUnlistedLanguage,
} from "helpers/core";
import { useAntTheme } from "hooks/useAntTheme";

interface LanguageSelectProps extends SelectProps {
  languages?:
    | string[]
    | Array<{
        value: string;
        label?: string;
        name?: string;
        description?: string;
        hasLocale?: boolean;
      }>;
  showIcon?: boolean;
  includeAuto?: boolean;
}

export const LanguageSelect = ({
  value,
  onChange,
  placeholder,
  allowClear = false,
  style = { width: "100%" },
  disabled = false,
  size = "middle",
  languages: customLanguages,
  showIcon = false,
  includeAuto = false,
  ...restProps
}: LanguageSelectProps) => {
  const t = useTranslate();
  const { getLocale } = useTranslation();
  const { theme } = useAntTheme();
  const { supportedLanguagesOptions, isLoading, isError } =
    useSystemConfiguration();
  const hasLoadError = !customLanguages && isError;
  const currentLocale = getLocale() ?? "en-US";

  // Labels match the project Wizard (Intl names); the default list is the backend one.
  const baseOptions = customLanguages
    ? customLanguages.map((lang) => {
        const langValue = typeof lang === "string" ? lang : lang.value;
        const label = typeof lang === "string" ? undefined : lang.label;
        return {
          value: langValue,
          label: label ?? getLanguageName(langValue, currentLocale, true),
        };
      })
    : supportedLanguagesOptions.map(({ value, label }) => ({ value, label }));

  const languageOptions = [
    ...(includeAuto
      ? [{ value: "auto", label: t("nativeLanguageNames.auto") }]
      : []),
    ...withUnlistedLanguage(
      withEnglishFirst(baseOptions),
      value,
      currentLocale
    ),
  ];

  const selectElement = (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowClear={allowClear}
      style={showIcon ? { flex: 1, minWidth: 0 } : style}
      disabled={disabled}
      size={size}
      options={languageOptions}
      loading={!customLanguages && isLoading}
      status={hasLoadError ? "error" : undefined}
      // popupRender, not notFoundContent: with includeAuto the list is never empty.
      notFoundContent={hasLoadError ? null : undefined}
      popupRender={
        hasLoadError
          ? (menu) => (
              <>
                {menu}
                <Typography.Text
                  type="danger"
                  style={{ display: "block", padding: "4px 12px" }}
                >
                  {t("languageSelect.loadError")}
                </Typography.Text>
              </>
            )
          : undefined
      }
      {...restProps}
    />
  );

  if (!showIcon) {
    return selectElement;
  }

  return (
    <Flex align="center" gap={8} style={style}>
      <GlobeSimpleIcon
        size={20}
        color={theme.colorTextSecondary}
        style={{ flexShrink: 0 }}
      />
      {selectElement}
    </Flex>
  );
};
